import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { getInboxColumnWidths, saveInboxColumnWidths } from "@/api/inboxPreferencesApi";
import {
  INBOX_CENTER_MIN_PX,
  INBOX_COLUMN_DEFAULTS,
  INBOX_COLUMN_LIMITS,
  INBOX_KEYBOARD_NUDGE_PX,
  INBOX_SPLITTER_WIDTH_PX,
} from "../inboxLayoutConfig";

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

async function persistWidths(left, right) {
  await saveInboxColumnWidths({ left, right });
}

/**
 * Reconcile left + right so center (flex) keeps at least INBOX_CENTER_MIN_PX.
 * @param {number} totalWidth host clientWidth
 */
function reconcileWidths(totalWidth, left, right) {
  const gutter = INBOX_SPLITTER_WIDTH_PX * 2;
  const cap = totalWidth - gutter - INBOX_CENTER_MIN_PX;
  const floor = INBOX_COLUMN_LIMITS.left.min + INBOX_COLUMN_LIMITS.right.min;
  if (totalWidth <= gutter + INBOX_CENTER_MIN_PX) {
    return [INBOX_COLUMN_LIMITS.left.min, INBOX_COLUMN_LIMITS.right.min];
  }
  if (cap < floor) {
    const scale = cap / floor;
    return [
      Math.max(INBOX_COLUMN_LIMITS.left.min, Math.floor(INBOX_COLUMN_LIMITS.left.min * scale)),
      Math.max(INBOX_COLUMN_LIMITS.right.min, Math.floor(INBOX_COLUMN_LIMITS.right.min * scale)),
    ];
  }

  let l = clamp(left, INBOX_COLUMN_LIMITS.left.min, INBOX_COLUMN_LIMITS.left.max);
  let r = clamp(right, INBOX_COLUMN_LIMITS.right.min, INBOX_COLUMN_LIMITS.right.max);
  if (l + r <= cap) return [l, r];

  const excess = l + r - cap;
  const share = l / (l + r);
  l = Math.max(INBOX_COLUMN_LIMITS.left.min, Math.round(l - excess * share));
  r = cap - l;
  if (r < INBOX_COLUMN_LIMITS.right.min) {
    r = INBOX_COLUMN_LIMITS.right.min;
    l = cap - r;
  }
  if (l < INBOX_COLUMN_LIMITS.left.min) {
    l = INBOX_COLUMN_LIMITS.left.min;
    r = cap - l;
  }
  l = clamp(l, INBOX_COLUMN_LIMITS.left.min, INBOX_COLUMN_LIMITS.left.max);
  r = clamp(r, INBOX_COLUMN_LIMITS.right.min, INBOX_COLUMN_LIMITS.right.max);
  return [l, r];
}

/**
 * Adjustable left/right column widths for the inbox shell (pointer + keyboard).
 * Center column is fluid between the two splitters.
 */
export function useInboxColumnWidths() {
  const containerRef = useRef(null);
  const [left, setLeft] = useState(INBOX_COLUMN_DEFAULTS.left);
  const [right, setRight] = useState(INBOX_COLUMN_DEFAULTS.right);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await getInboxColumnWidths();
      if (cancelled || !data) return;
      setLeft(data.left);
      setRight(data.right);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const leftRef = useRef(left);
  const rightRef = useRef(right);
  leftRef.current = left;
  rightRef.current = right;

  const dragRef = useRef(null);

  const applyReconcile = useCallback(() => {
    const host = containerRef.current;
    if (!host) return;
    const total = host.getBoundingClientRect().width;
    if (total <= 0) return;
    const [nl, nr] = reconcileWidths(total, leftRef.current, rightRef.current);
    if (nl !== leftRef.current) setLeft(nl);
    if (nr !== rightRef.current) setRight(nr);
  }, []);

  useLayoutEffect(() => {
    applyReconcile();
  }, [applyReconcile]);

  useEffect(() => {
    const host = containerRef.current;
    if (!host || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      applyReconcile();
    });
    ro.observe(host);
    return () => ro.disconnect();
  }, [applyReconcile]);

  const persist = useCallback(() => {
    persistWidths(leftRef.current, rightRef.current);
  }, []);

  const applyLeftDelta = useCallback((dx) => {
    const host = containerRef.current;
    if (!host) return;
    const total = host.getBoundingClientRect().width;
    const nextLeft = leftRef.current + dx;
    const gutter = INBOX_SPLITTER_WIDTH_PX * 2;
    const maxLeft = total - gutter - rightRef.current - INBOX_CENTER_MIN_PX;
    const clamped = clamp(nextLeft, INBOX_COLUMN_LIMITS.left.min, Math.min(INBOX_COLUMN_LIMITS.left.max, maxLeft));
    setLeft(clamped);
  }, []);

  const applyRightDelta = useCallback((dx) => {
    const host = containerRef.current;
    if (!host) return;
    const total = host.getBoundingClientRect().width;
    const nextRight = rightRef.current - dx;
    const gutter = INBOX_SPLITTER_WIDTH_PX * 2;
    const maxRight = total - gutter - leftRef.current - INBOX_CENTER_MIN_PX;
    const clamped = clamp(nextRight, INBOX_COLUMN_LIMITS.right.min, Math.min(INBOX_COLUMN_LIMITS.right.max, maxRight));
    setRight(clamped);
  }, []);

  const resetLeftDefault = useCallback(() => {
    const host = containerRef.current;
    const total = host?.getBoundingClientRect().width ?? 1440;
    const [nl, nr] = reconcileWidths(total, INBOX_COLUMN_DEFAULTS.left, rightRef.current);
    setLeft(nl);
    setRight(nr);
    persistWidths(nl, nr);
  }, []);

  const resetRightDefault = useCallback(() => {
    const host = containerRef.current;
    const total = host?.getBoundingClientRect().width ?? 1440;
    const [nl, nr] = reconcileWidths(total, leftRef.current, INBOX_COLUMN_DEFAULTS.right);
    setLeft(nl);
    setRight(nr);
    persistWidths(nl, nr);
  }, []);

  const beginDrag = useCallback((edge) => {
    dragRef.current = { edge, lastClientX: null };
  }, []);

  const moveDrag = useCallback(
    (clientX) => {
      const d = dragRef.current;
      if (!d) return;
      if (d.lastClientX == null) {
        d.lastClientX = clientX;
        return;
      }
      const dx = clientX - d.lastClientX;
      d.lastClientX = clientX;
      if (d.edge === "left") applyLeftDelta(dx);
      else applyRightDelta(dx);
    },
    [applyLeftDelta, applyRightDelta]
  );

  const endDrag = useCallback(() => {
    dragRef.current = null;
    persist();
  }, [persist]);

  const leftGripProps = useMemo(
    () => ({
      "data-inbox-splitter": "left",
      role: "separator",
      "aria-orientation": "vertical",
      "aria-label": "Resize conversation list column",
      "aria-valuenow": Math.round(left),
      "aria-valuemin": INBOX_COLUMN_LIMITS.left.min,
      "aria-valuemax": INBOX_COLUMN_LIMITS.left.max,
      tabIndex: 0,
      title: "Drag to resize · Double-click resets this column · Arrow keys nudge (Shift = fine)",
      className:
        "group relative z-10 flex h-full min-h-0 w-full shrink-0 cursor-col-resize select-none items-stretch justify-center border-0 bg-transparent p-0 outline-none touch-none hover:bg-slate-200/40 focus-visible:bg-slate-200/50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-400/50",
      onPointerDown: (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        beginDrag("left");
        e.currentTarget.setPointerCapture(e.pointerId);
        moveDrag(e.clientX);
      },
      onPointerMove: (e) => {
        if (!dragRef.current || dragRef.current.edge !== "left") return;
        if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
        moveDrag(e.clientX);
      },
      onPointerUp: (e) => {
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
          e.currentTarget.releasePointerCapture(e.pointerId);
        }
        endDrag();
      },
      onLostPointerCapture: () => {
        endDrag();
      },
      onDoubleClick: (e) => {
        e.preventDefault();
        resetLeftDefault();
      },
      onKeyDown: (e) => {
        const step = e.shiftKey ? 4 : INBOX_KEYBOARD_NUDGE_PX;
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          applyLeftDelta(-step);
          persist();
        } else if (e.key === "ArrowRight") {
          e.preventDefault();
          applyLeftDelta(step);
          persist();
        } else if (e.key === "Home") {
          e.preventDefault();
          resetLeftDefault();
        }
      },
    }),
    [left, beginDrag, moveDrag, endDrag, resetLeftDefault, applyLeftDelta, persist]
  );

  const rightGripProps = useMemo(
    () => ({
      "data-inbox-splitter": "right",
      role: "separator",
      "aria-orientation": "vertical",
      "aria-label": "Resize customer details column",
      "aria-valuenow": Math.round(right),
      "aria-valuemin": INBOX_COLUMN_LIMITS.right.min,
      "aria-valuemax": INBOX_COLUMN_LIMITS.right.max,
      tabIndex: 0,
      title: "Drag to resize · Double-click resets this column · Arrow keys nudge (Shift = fine)",
      className:
        "group relative z-10 flex h-full min-h-0 w-full shrink-0 cursor-col-resize select-none items-stretch justify-center border-0 bg-transparent p-0 outline-none touch-none hover:bg-slate-200/40 focus-visible:bg-slate-200/50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-400/50",
      onPointerDown: (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        beginDrag("right");
        e.currentTarget.setPointerCapture(e.pointerId);
        moveDrag(e.clientX);
      },
      onPointerMove: (e) => {
        if (!dragRef.current || dragRef.current.edge !== "right") return;
        if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
        moveDrag(e.clientX);
      },
      onPointerUp: (e) => {
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
          e.currentTarget.releasePointerCapture(e.pointerId);
        }
        endDrag();
      },
      onLostPointerCapture: () => {
        endDrag();
      },
      onDoubleClick: (e) => {
        e.preventDefault();
        resetRightDefault();
      },
      onKeyDown: (e) => {
        const step = e.shiftKey ? 4 : INBOX_KEYBOARD_NUDGE_PX;
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          applyRightDelta(step);
          persist();
        } else if (e.key === "ArrowRight") {
          e.preventDefault();
          applyRightDelta(-step);
          persist();
        } else if (e.key === "Home") {
          e.preventDefault();
          resetRightDefault();
        }
      },
    }),
    [right, beginDrag, moveDrag, endDrag, resetRightDefault, applyRightDelta, persist]
  );

  return {
    containerRef,
    leftWidth: left,
    rightWidth: right,
    leftGripProps,
    rightGripProps,
  };
}
